import {
  APP_INITIALIZER,
  ApplicationConfig,
  importProvidersFrom,
  LOCALE_ID,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter } from '@angular/router';

import { registerLocaleData } from '@angular/common';
import localeBr from '@angular/common/locales/pt';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { CdsInputsModule } from '@celerofinancas/design-system';
import {
  provideSessionReplay,
  provideTrackingDataContributor,
  SessionReplayConfig,
  TrackingService,
} from '@celerofinancas/service-utils';
import { CompanyState } from '@celerofinancas/state-management';
import { UiDirectivesModule } from '@celerofinancas/ui-directives';
import {
  ActiveCompanyStateToken,
  FeatureFlagService,
  UiFeatureFlagModule,
} from '@celerofinancas/ui-feature-flag';
import { LoadersModule } from '@celerofinancas/ui-loaders';
import { UiOAuthModule } from '@celerofinancas/ui-oauth';
import { WebsocketModule } from '@celerofinancas/ui-websocket';
import { environment } from '@environments/environment';
import { OAuthConfiguration } from '@environments/oauth';
import { Store } from '@ngxs/store';
import { BufferedTrackingService } from '@shared/services/buffered-tracking.service';
import { CaixaTrackingContributor } from '@shared/services/caixa-tracking.contributor';
import { StoreModule } from '@shared/store.module';
import { provideNgxMask } from 'ngx-mask';
import { routes } from './app.routes';
import { INTERCEPTORS_LIST } from './configs/interceptors';
import { APPLICATION_TOKENS } from './configs/tokens';

registerLocaleData(localeBr, 'pt');

/**
 * Preload feature flags
 *
 * @param flag feature flag service
 * @returns promise
 */
function preloadFeatureFlags(flag: FeatureFlagService): () => Promise<void> {
  return () => flag.init();
}

export const appConfig: ApplicationConfig = {
  providers: [
    {
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: preloadFeatureFlags,
      deps: [FeatureFlagService],
    },
    { provide: TrackingService, useExisting: BufferedTrackingService },
    provideTrackingDataContributor(CaixaTrackingContributor),
    { provide: LOCALE_ID, useValue: 'pt' },
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideAnimationsAsync(),
    provideNgxMask(),
    ...provideSessionReplay({ config: environment.sessionReplay as SessionReplayConfig }),
    ...INTERCEPTORS_LIST,
    ...APPLICATION_TOKENS,
    importProvidersFrom(
      CdsInputsModule,
      StoreModule,
      LoadersModule,
      UiDirectivesModule,
      UiFeatureFlagModule.forRoot(environment?.featureFlag, [
        {
          provide: ActiveCompanyStateToken,
          useFactory: (store: Store) => store.select(CompanyState.getActiveCompany),
          deps: [Store],
        },
      ]),
      UiOAuthModule.forRoot(environment, OAuthConfiguration),
      WebsocketModule.forRoot({
        url: environment.webSocketUrl,
        path: '/socket.io',
        transports: ['websocket'],
        defaultNamespace: '/',
        enabled: true,
        tokenKey: 'celero_access_token',
        auth: async () => ({ token: sessionStorage.getItem('celero_access_token') || undefined }),
      }),
    ),
  ],
};
